/* eslint-disable no-underscore-dangle */

import PropTypes from 'prop-types';
import round from 'lodash/round';
import { ScatterplotLayer } from '@deck.gl/layers';
import { createClusterWorker } from './createClusterWorker';
import BaseClusterCompositeLayer from './BaseClusterCompositeLayer';
import { createAvailableAllocationRatioLayer } from './createAvailableAllocationRatioLayer';
import { createMapMarkerIconWithTextLayer } from './createMapMarkerIconWithTextLayer';
import { isObjectClickable } from '../utils/getIsMapElementClickable';
import { HALO_BORDER_ALPHA, HALO_FILL_ALPHA } from '../utils/mapColors';
import { MAP_ELEMENT_RADIUS_SCALE } from '../utils/getMapElementSize';
import { getAssetBaseRgb } from './utils';

const BASE_OUTLINES_SETTINGS = {
  pickable: false,
  stroked: true,
  filled: false,
  radiusUnits: 'pixels',
  lineWidthUnits: 'pixels',
};

export default class ClusterAssetsCompositeLayer extends BaseClusterCompositeLayer {
  constructor(props) {
    super(props, 'ClusterAssetsCompositeLayer');
  }

  initializeState(context) {
    super.initializeState(context);

    this.setState({
      data: [],
      hoveredCluster: null,
      isHoveredByCluster: false,
      worker: createClusterWorker(),
      latestRequestId: 0,
      hasWorkerData: false,
    });
    this.clusterMap = new Map();
  }

  updateState({ props, changeFlags }) {
    this.state.worker.onmessage = this.handleWorkerMessage;

    super.updateState({ props, changeFlags });

    if (changeFlags.somethingChanged) {
      this.refreshDerivedFromData();
      if (props.selectedCluster !== this.props.selectedCluster) {
        this.availableAllocationRatioLayer = null;
        this.mapMarkerIconWithTextLayer = null;
        this.selectedClusterOutlineLayer = null;
        this.hoveredClusterOutlineLayer = null;
      }

      if (props.activeColorFilter !== this.props.activeColorFilter) {
        this.availableAllocationRatioLayer = null;
        this.lastActiveColorFilter = undefined;
      }

      if (!props.lassoFurnitureCodes) return;

      if (props.hoveredFurnitureCode) {
        const externalHoveredCluster = this.findClusterByFurnitureCode(props.hoveredFurnitureCode);

        if (externalHoveredCluster) {
          this.setState({ hoveredCluster: externalHoveredCluster });
          this.hoveredClusterOutlineLayer = null;
        }
      } else if (!this.state.isHoveredByCluster) {
        this.setState({ hoveredCluster: null });
        this.hoveredClusterOutlineLayer = null;
      }
    }
  }

  addClusterByFurnitureCode = (furnitureCode, cluster) => {
    if (furnitureCode) {
      this.clusterMap.set(`furnitureCode:${furnitureCode}`, cluster);
    }
  };

  addClusterByFrameId = (properties, cluster, lassoFurnitureCodes) => {
    if (properties.frameId) {
      this.clusterMap.set(`frameId:${properties.frameId}`, cluster);
    }
    if (lassoFurnitureCodes && properties.furnitureCode && lassoFurnitureCodes.has(properties.furnitureCode)) {
      this.addClusterByFurnitureCode(properties.furnitureCode, cluster);
    }
  };

  addCluster = (properties, cluster, lassoFurnitureCodes) => {
    if (properties.cluster_id) {
      this.clusterMap.set(`cluster_id:${properties.cluster_id}`, cluster);
    }
    if (lassoFurnitureCodes && properties.assets?.length > 0) {
      properties.assets.forEach((asset) => {
        if (asset.furnitureCode && lassoFurnitureCodes.has(asset.furnitureCode)) {
          this.addClusterByFurnitureCode(asset.furnitureCode, cluster);
        }
      });
    }
  };

  buildClusterMap = () => {
    const { data } = this.state;
    const { lassoFurnitureCodes } = this.props;

    this.clusterMap = new Map();

    data.forEach((cluster) => {
      const { properties } = cluster;
      if (!properties) return;

      this.clusterMap.set(properties, cluster);

      if (!properties.cluster) {
        this.addClusterByFrameId(properties, cluster, lassoFurnitureCodes);
      } else {
        this.addCluster(properties, cluster, lassoFurnitureCodes);
      }
    });
  };

  findHoveredCluster = (object) => {
    if (!object || !isObjectClickable(object)) return null;

    const { isClusterClickable } = this.props;

    let hoveredCluster = this.clusterMap.get(object);

    if (!hoveredCluster && object.frameId) {
      hoveredCluster = this.clusterMap.get(`frameId:${object.frameId}`);
    }

    if (!hoveredCluster && object.cluster && object.cluster_id) {
      hoveredCluster = this.clusterMap.get(`cluster_id:${object.cluster_id}`);
    }

    return hoveredCluster && isClusterClickable(hoveredCluster) ? hoveredCluster : null;
  };

  getPickingInfo({ info, mode }) {
    const result = super.getPickingInfo({ info, mode });

    if (mode === 'hover') {
      const hoveredCluster = this.findHoveredCluster(result.object);
      if (hoveredCluster !== this.state.hoveredCluster) {
        this.setState({ hoveredCluster });
        this.setState({ isHoveredByCluster: hoveredCluster !== null });
        this.hoveredClusterOutlineLayer = null;
      }
    }

    return result;
  }

  performUpdate({ props: { data } }) {
    const currentZoomLevel = round(this.context.viewport.zoom, 1);
    const dataChange = this.checkDataChange(data);
    const hasZoomChanged = currentZoomLevel !== this.state.zoom;

    if (!dataChange.hasDataChanged && !hasZoomChanged) return;

    const shouldSendData = dataChange.hasDataChanged || !this.state.hasWorkerData;

    if (dataChange.hasDataChanged) {
      this.setState({ loadedDataHash: dataChange.currentDataHash });
    }

    const requestId = this.state.latestRequestId + 1;
    this.setState({ latestRequestId: requestId, hasWorkerData: true });

    this.state.worker.postMessage({
      type: 'CLUSTER',
      requestId,
      payload: {
        points: shouldSendData ? data : null,
        clusterRadius: this.props.clusterRadius,
        zoom: currentZoomLevel,
      },
    });
  }

  handleWorkerMessage = (event) => {
    const { type, payload, requestId, zoom } = event.data;

    if (type !== 'CLUSTERED' || requestId !== this.state.latestRequestId) return;

    this.setState({ data: payload, zoom });
    this.refreshDerivedFromData();
    this.props.onViewportDataChange?.(payload);
  };

  refreshDerivedFromData = () => {
    this.setState({
      iconData: (this.state.data ?? []).filter((mapElement) => mapElement.isVisible && !mapElement.properties?.cluster),
    });
    this.buildClusterMap();
  };

  renderLayers() {
    this.createLayers();
    const layers = [];

    if (this.hoveredClusterOutlineLayer) {
      layers.push(this.hoveredClusterOutlineLayer);
    }

    if (this.selectedClusterOutlineLayer) {
      layers.push(this.selectedClusterOutlineLayer);
    }

    if (this.shadowLayer) {
      layers.push(this.shadowLayer);
    }

    if (this.borderLayer) {
      layers.push(this.borderLayer);
    }

    if (this.availableAllocationRatioLayer) {
      layers.push(this.availableAllocationRatioLayer);
    }

    if (this.mapMarkerIconWithTextLayer) {
      layers.push(this.mapMarkerIconWithTextLayer);
    }

    return layers;
  }

  getAssetKey = (asset) => asset.furnitureCode ?? `${asset.longitude}, ${asset.latitude}`;

  isMatchingFurnitureCode = (cluster, furnitureCode) => {
    if (!cluster?.properties) return false;

    if (cluster.properties.furnitureCode === furnitureCode) {
      return true;
    }

    if (cluster.properties.assets.length > 0) {
      return cluster.properties.assets.some((asset) => asset.furnitureCode === furnitureCode);
    }

    return false;
  };

  hasMatchingFurnitureCodeInsideLassoSelection = (properties, lassoFurnitureCodes) => {
    if (!properties) return false;

    if (properties.furnitureCode && lassoFurnitureCodes.has(properties.furnitureCode)) {
      return true;
    }

    if (properties.assets?.length) {
      return properties.assets.some((asset) => asset.furnitureCode && lassoFurnitureCodes.has(asset.furnitureCode));
    }

    return false;
  };

  searchClustersWithPolygonFilter = (data, furnitureCode, lassoFurnitureCodes, isClusterClickable) => {
    const cluster = this.clusterMap.get(`furnitureCode:${furnitureCode}`);

    if (cluster && isClusterClickable(cluster)) {
      return cluster;
    }

    for (let i = 0; i < data.length; i++) {
      const clusterItem = data[i];

      if (
        this.hasMatchingFurnitureCode(clusterItem.properties, lassoFurnitureCodes) &&
        this.matchesFurnitureCode(clusterItem, furnitureCode) &&
        isClusterClickable(clusterItem)
      ) {
        return clusterItem;
      }
    }
    return null;
  };

  findClusterByFurnitureCode = (furnitureCode) => {
    const { data } = this.state;
    const { isClusterClickable, lassoFurnitureCodes } = this.props;

    if (lassoFurnitureCodes && lassoFurnitureCodes.has(furnitureCode)) {
      return this.searchClustersWithPolygonFilter(data, furnitureCode, lassoFurnitureCodes, isClusterClickable);
    }

    return null;
  };

  isClusterSelected = (cluster) => {
    const { selectedCluster } = this.props;

    if (!selectedCluster?.furnitureCode || !cluster?.properties) return false;

    if (!cluster.properties.cluster) {
      return this.getAssetKey(cluster.properties) === selectedCluster.furnitureCode;
    }

    const clusterAssets = cluster.properties.assets || [];

    return (
      clusterAssets.length > 0 &&
      clusterAssets.some((asset) => this.getAssetKey(asset) === selectedCluster.furnitureCode)
    );
  };

  createOutlineLayer = ({
    id,
    data,
    updateTrigger,
    fillColor,
    lineColor,
    lineWidth,
    isAlwaysVisible = false,
    filled = false,
    radiusScale = MAP_ELEMENT_RADIUS_SCALE,
  }) => {
    const { getPosition, getMapElementSize, updateTriggers, isClusterClickable } = this.props;

    return new ScatterplotLayer({
      id,
      data,
      lineWidthMinPixels: lineWidth,
      getPosition,
      getRadius: (element) => {
        if (isAlwaysVisible && !isClusterClickable(element)) return 0;

        return getMapElementSize(element) * radiusScale;
      },
      getLineColor: lineColor,
      getFillColor: fillColor,
      updateTriggers: {
        ...updateTriggers,
        getFillColor: [updateTrigger],
        getLineColor: [updateTrigger],
        getRadius: [updateTrigger],
      },
      ...BASE_OUTLINES_SETTINGS,
      filled,
    });
  };

  createLayers = () => {
    const {
      impressions,
      frames,
      clusters,
      rangeSegments,
      isReadOnly,
      isDirectSales,
      isPriced,
      isSharedCampaign,
      activeColorFilter,
      getPosition,
      updateTriggers,
      getMapElementSize,
      selectedCluster,
      routeFrameCodes,
    } = this.props;
    const { data } = this.state;

    if (!data?.length) return;

    const shouldShowSelectionState = isDirectSales || isSharedCampaign;

    if (this.lastCreateLayersData !== data) {
      this.lastCreateLayersData = data;
      this.mapMarkerIconWithTextLayer = null;
      this.borderLayer = null;
      this.shadowLayer = null;
    }

    if (this.lastGetMapElementSize !== getMapElementSize) {
      this.lastGetMapElementSize = getMapElementSize;
      this.mapMarkerIconWithTextLayer = null;
      this.availableAllocationRatioLayer = null;
      this.borderLayer = null;
      this.shadowLayer = null;
    }

    if (this.lastRouteFrameCodes !== routeFrameCodes) {
      this.lastRouteFrameCodes = routeFrameCodes;
      this.mapMarkerIconWithTextLayer = null;
      this.availableAllocationRatioLayer = null;
    }

    if (
      !this.availableAllocationRatioLayer ||
      this.lastRatioLayerData !== data ||
      this.lastActiveColorFilter !== activeColorFilter
    ) {
      this.lastRatioLayerData = data;
      this.lastActiveColorFilter = activeColorFilter;
      this.availableAllocationRatioLayer = createAvailableAllocationRatioLayer(
        data,
        routeFrameCodes || [],
        {
          impressions,
          frames,
          clusters,
          rangeSegments,
          isReadOnly,
          isDirectSales,
          isPriced,
          isSharedCampaign,
          activeColorFilter,
          shouldShowSelectionState,
        },
        getPosition,
        getMapElementSize,
        updateTriggers,
      );
    }

    if (!this.mapMarkerIconWithTextLayer) {
      this.mapMarkerIconWithTextLayer = createMapMarkerIconWithTextLayer(
        data,
        routeFrameCodes || [],
        getPosition,
        updateTriggers,
        rangeSegments?.getTextRGBColorByIndexValue,
        getMapElementSize,
        shouldShowSelectionState,
      );
    }

    if (!this.borderLayer) {
      this.borderLayer = this.createOutlineLayer({
        id: 'border-layer',
        data,
        updateTriggers,
        fillColor: [255, 255, 255],
        lineColor: [255, 255, 255],
        lineWidth: 3,
        isAlwaysVisible: true,
      });
    }

    if (!this.shadowLayer) {
      this.shadowLayer = this.createOutlineLayer({
        id: 'shadow-layer',
        data,
        updateTriggers,
        lineColor: [0, 0, 0, 60],
        fillColor: [0, 0, 0, 0],
        lineWidth: 5,
        isAlwaysVisible: true,
      });
    }

    this.createHaloLayers(data, selectedCluster, {
      isSharedCampaign,
      isReadOnly,
      isDirectSales,
      impressions,
      rangeSegments,
      routeFrameCodes: routeFrameCodes || [],
      isPriced,
      activeColorFilter,
    });
  };

  createHaloLayers = (data, selectedCluster, colorConfig) => {
    const getHaloFillColor = (element) => [...getAssetBaseRgb(element, colorConfig), HALO_FILL_ALPHA];
    const getHaloLineColor = (element) => [...getAssetBaseRgb(element, colorConfig), HALO_BORDER_ALPHA];

    const { hoveredCluster } = this.state;

    if (hoveredCluster && !this.isClusterSelected(hoveredCluster)) {
      const updateTrigger = hoveredCluster.properties?.cluster_id || hoveredCluster.properties?.frameId;
      this.hoveredClusterOutlineLayer = this.createOutlineLayer({
        id: 'hovered-cluster-outline-layer',
        data: [hoveredCluster],
        updateTrigger,
        fillColor: getHaloFillColor,
        lineColor: getHaloLineColor,
        lineWidth: 1,
        filled: true,
        radiusScale: 0.25,
      });
    } else {
      this.hoveredClusterOutlineLayer = null;
    }

    if (selectedCluster?.furnitureCode) {
      const selectedClusterData = data.filter((cluster) => this.isClusterSelected(cluster));
      this.selectedClusterOutlineLayer =
        selectedClusterData.length > 0
          ? this.createOutlineLayer({
              id: 'selected-cluster-outline-layer',
              data: selectedClusterData,
              updateTrigger: selectedCluster.furnitureCode,
              fillColor: getHaloFillColor,
              lineColor: getHaloLineColor,
              lineWidth: 1,
              filled: true,
              radiusScale: 0.25,
            })
          : null;
    } else {
      this.selectedClusterOutlineLayer = null;
    }
  };

  _finalize() {
    this.state.worker?.terminate();
    super._finalize();
  }
}

ClusterAssetsCompositeLayer.layerName = 'ClusterAssetsCompositeLayer';
ClusterAssetsCompositeLayer.propTypes = {
  ...BaseClusterCompositeLayer.propTypes,
  getIconSize: PropTypes.func,
  getTextSize: PropTypes.func,
  isClusterClickable: PropTypes.func.isRequired,
  hoveredFurnitureCode: PropTypes.string,
  lassoFurnitureCodes: PropTypes.instanceOf(Set),
};

/* eslint-enable no-underscore-dangle */
